import { sha256HexOfText } from "./sha256.js";
// Text rules shared by the import normalizer and the duplicate matcher (Phase 3.4). Pure: no I/O, no
// clock, no locale. Plain words: these functions tidy cell text and make the comparison keys that two
// spellings of one name share.
const ZERO_WIDTH = /[​-‍⁠﻿]/g;
// biome-ignore lint/suspicious/noControlCharactersInRegex: the point is to remove them.
const CONTROL = /[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]/g;
const MARKS = /\p{M}/gu;
/** Cell values that mean "nothing here". Compared after the cell is tidied and lower-cased. `0` is not one. */
export const IMPORT_NULL_TOKENS = new Set([
    "",
    "-",
    "--",
    "—",
    "n/a",
    "na",
    "null",
    "none",
    "tbd",
    "tba",
    "?",
]);
/**
 * The tidy text of a cell: Unicode NFC, no zero-width or control characters, runs of white space made
 * one space, the ends trimmed. A multi-line field keeps its line breaks (at most one blank line).
 */
export function cleanText(raw, multiline = false) {
    const clean = raw.normalize("NFC").replace(ZERO_WIDTH, "").replace(CONTROL, "");
    if (!multiline)
        return clean.replace(/\s+/g, " ").trim();
    return clean
        .replace(/\r\n?/g, "\n")
        .split("\n")
        .map((line) => line.replace(/[^\S\n]+/g, " ").trim())
        .join("\n")
        .replace(/\n{3,}/g, "\n\n")
        .trim();
}
/** The tidy text of a cell, or `null` when the cell is empty or a null token (`N/A`, `-`, `TBD`, ...). */
export function cleanCell(raw, multiline = false) {
    const text = cleanText(raw, multiline);
    return IMPORT_NULL_TOKENS.has(text.toLowerCase()) ? null : text;
}
/**
 * The comparison key of a name: Unicode NFKD without combining marks, lower case, `&` as `and`, no
 * leading `the `, then only letters and digits (any script). `Blue Frog` and `the BLUE-frog` share a key.
 */
export function nameKey(value) {
    const folded = value
        .normalize("NFKD")
        .replace(MARKS, "")
        .toLowerCase()
        .replace(/&/g, " and ")
        .replace(/\s+/g, " ")
        .trim()
        .replace(/^the /, "");
    return folded.replace(/[^\p{L}\p{N}]+/gu, "");
}
/**
 * A URL slug of a name: ASCII words joined by single hyphens, at most 120 characters. A name with no
 * Latin letter or digit (for example a Japanese name) gets `p-` and 12 hex characters of its key hash, so
 * the slug is stable and never empty.
 */
export function slugOf(value) {
    const slug = value
        .normalize("NFKD")
        .replace(MARKS, "")
        .toLowerCase()
        .replace(/&/g, " and ")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "")
        .slice(0, 120)
        .replace(/-+$/g, "");
    return slug === "" ? `p-${sha256HexOfText(nameKey(value)).slice(0, 12)}` : slug;
}
/** The length of a text in code points (the unit the database `char_length` counts). */
export const codePointLength = (value) => [...value].length;
